import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  IconButton,
  Paper,
  Chip,
  Rating,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ShoppingCart as CartIcon,
  ExpandMore as ExpandMoreIcon,
  DesignServices as DesignIcon,
  Build as BuildIcon,
  CloudDownload as CloudDownloadIcon,
  Star as StarIcon,
  LocalShipping as ShippingIcon,
  Support as SupportIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Undo as UndoIcon
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Product } from '../types';
import { ProductService } from '../services/productService';
import { userService } from '../services/userService';
import { User } from '../types';
import { logAuthStatus, isAdminFromToken, isTokenValid } from '../utils/authHelpers';

const CATEGORIES = [
  'icons',
  'logos',
  'illustrations',
  'graphics',
  'images',
  'ebooks',
  'software',
  'templates',
  'music',
  'videos',
  'courses',
  'other'
];

interface ProductListingProps {
  onAddToCart: (product: Product) => void;
  onDownload: (product: Product) => void;
  onRequireLogin?: () => void;
  user?: User | null;
}

const ProductListing: React.FC<ProductListingProps> = ({
  onAddToCart,
  onDownload,
  onRequireLogin,
  user
}) => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoadingFavorite, setIsLoadingFavorite] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<{
    name?: string;
    description?: string;
    price?: number;
    category?: string;
    tags?: string; // Store as string for form input
    previewImages?: File[];
    previewPreviews?: string[]; // Object URLs for preview
    existingPreviewImages?: Array<{ s3Key?: string; url?: string; caption?: string }>; // Existing images from product
    deletedPreviewImages?: string[]; // Array of s3Keys to delete
    downloadFile?: File | null;
    downloadPreview?: string | null;
  }>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [reviews, setReviews] = useState<Array<{
    id: string;
    rating: number;
    text: string;
    reviewerName: string;
    date: string;
    avatar: string | null;
    itemQuality?: number;
    shipping?: number;
    customerService?: number;
  }>>([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [averageRating, setAverageRating] = useState(0);
  const [avgItemQuality, setAvgItemQuality] = useState(0);
  const [avgShipping, setAvgShipping] = useState(0);
  const [avgCustomerService, setAvgCustomerService] = useState(0);
  const productService = new ProductService();

  const reviewCount = reviews.length;
  const buyerHighlights = ['Easy to use', 'Great product', 'Would recommend', 'Looks great'];

  useEffect(() => {
    if (productId) {
      fetchProduct();
      checkFavoriteStatus();
      fetchReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      // Add cache-busting query parameter to ensure fresh data
      const cacheBuster = `?t=${Date.now()}`;
      const response = await productService.getProduct(productId! + cacheBuster);
      if (response.success) {
        const productData = response.data as any;
        console.log('Fetched product after update:', {
          productId,
          previewImagesCount: productData?.previewImages?.length || 0,
          imagesCount: productData?.images?.length || 0,
          previewImages: productData?.previewImages?.map((img: any) => img.s3Key || 'no-s3key')
        });
        setProduct(response.data);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkFavoriteStatus = async () => {
    if (!userService.isAuthenticated()) return;
    try {
      const response = await productService.isFavorite(productId!);
      if (response.success && response.data !== null && response.data !== undefined) {
        setIsFavorite(!!response.data);
      }
    } catch (error) {
      console.error('Error checking favorite status:', error);
    }
  };

  const handleToggleFavorite = async () => {
    if (!userService.isAuthenticated()) {
      if (onRequireLogin) {
        onRequireLogin();
      }
      return;
    }

    setIsLoadingFavorite(true);
    try {
      if (isFavorite) {
        await productService.removeFromFavorites(productId!);
        setIsFavorite(false);
      } else {
        await productService.addToFavorites(productId!);
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsLoadingFavorite(false);
    }
  };

  const fetchReviews = async () => {
    if (!productId) return;

    try {
      setReviewsLoading(true);
      const response = await productService.getReviews(productId);
      if (response.success && response.data) {
        setReviews(response.data.reviews);
        setAverageRating(parseFloat(response.data.averageRating) || 0);
        setAvgItemQuality(parseFloat(response.data.avgItemQuality) || 0);
        setAvgShipping(parseFloat(response.data.avgShipping) || 0);
        setAvgCustomerService(parseFloat(response.data.avgCustomerService) || 0);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      // Keep empty reviews array on error
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleEditClick = async () => {
    // Debug: Log authentication status when opening edit dialog
    logAuthStatus('Edit Product Click');
    
    // Quick check before opening dialog
    const token = localStorage.getItem('authToken');
    if (!token || !isTokenValid(token)) {
      setSnackbar({ 
        open: true, 
        message: 'Your session has expired. Please log in again.', 
        severity: 'error' 
      });
      return;
    }

    if (!isAdminFromToken(token) && user?.role !== 'admin') {
      setSnackbar({ 
        open: true, 
        message: 'You do not have permission to edit products. Admin access required.', 
        severity: 'error' 
      });
      return;
    }

    if (!product || !productId) {
      setSnackbar({ 
        open: true, 
        message: 'Product data is not available. Please refresh the page.', 
        severity: 'error' 
      });
      return;
    }

    // Fetch full product data to get previewImages with s3Keys
    try {
      const response = await productService.getProduct(productId);
      console.log('Full API response:', response);
      
      // Check if product doesn't exist (404)
      if (!response.success) {
        const errorMessage = response.message || response.error || 'Unknown error';
        
        // If it's a 404 or "not found" error, don't open the dialog
        if (errorMessage.toLowerCase().includes('not found') || 
            errorMessage.toLowerCase().includes('404') ||
            response.error?.toLowerCase().includes('not found')) {
          setSnackbar({ 
            open: true, 
            message: 'Product not found. It may have been deleted. Refreshing page...', 
            severity: 'error' 
          });
          // Refresh the page after a short delay to show the error message
          setTimeout(() => {
            window.location.reload();
          }, 2000);
          return;
        }
        
        // For other errors, show warning but continue with cached data
        setSnackbar({ 
          open: true, 
          message: `Could not fetch latest product data: ${errorMessage}. Using cached data.`, 
          severity: 'warning' 
        });
      }
      
      if (response.success && response.data) {
        const productData = response.data as any;
        console.log('Product data from API:', productData);
        console.log('previewImages from API:', productData.previewImages);
        console.log('images array from API:', productData.images);
        
        // Get existing preview images - check multiple possible locations
        let existingPreviews = productData.previewImages || [];
        
        // If previewImages is empty but images array exists, try to reconstruct
        if (existingPreviews.length === 0 && productData.images && productData.images.length > 0) {
          console.log('previewImages is empty, but images array exists. Attempting to reconstruct...');
          existingPreviews = productData.images.map((url: string, index: number) => ({
            s3Key: undefined,
            url: url,
            caption: `Preview ${index + 1}`
          }));
        }
        
        console.log('Final existing previews:', existingPreviews);
        
        // Map existing preview images with their URLs
        const existingPreviewImages = existingPreviews.map((img: any, index: number) => {
          const imageUrl = img.url || productData.images?.[index] || productData.image || product.image;
          return {
            s3Key: img.s3Key,
            url: imageUrl,
            caption: img.caption || `Preview ${index + 1}`
          };
        });
        
        console.log('Mapped existing preview images:', existingPreviewImages);
        
        setEditingProduct({
          name: productData.name || product.name,
          description: productData.description || product.description,
          price: productData.price || product.price,
          category: productData.category || product.category,
          tags: (productData.tags || product.tags || []).join(', '),
          previewImages: [],
          previewPreviews: [],
          existingPreviewImages: existingPreviewImages,
          deletedPreviewImages: [],
          downloadFile: null,
          downloadPreview: null
        });
        setEditDialogOpen(true);
      } else {
        // Fallback: use existing product data if API call failed but we have product in state
        console.warn('API response missing data, using existing product state:', response);
        setEditingProduct({
          name: product.name,
          description: product.description,
          price: product.price,
          category: product.category,
          tags: product.tags.join(', '),
          previewImages: [],
          previewPreviews: [],
          existingPreviewImages: product.images?.map((url: string, index: number) => ({
            s3Key: undefined,
            url: url,
            caption: `Preview ${index + 1}`
          })) || [],
          deletedPreviewImages: [],
          downloadFile: null,
          downloadPreview: null
        });
        setEditDialogOpen(true);
      }
    } catch (error: any) {
      console.error('Error fetching product details:', error);
      
      // Check if it's a 404 error
      const errorMessage = error?.message || String(error);
      if (errorMessage.includes('404') || errorMessage.toLowerCase().includes('not found')) {
        setSnackbar({ 
          open: true, 
          message: 'Product not found. It may have been deleted. Refreshing page...', 
          severity: 'error' 
        });
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return;
      }
      
      // For other errors, use existing product data as fallback
      setSnackbar({ 
        open: true, 
        message: 'Could not fetch latest product data. Using cached data. Some features may be limited.', 
        severity: 'warning' 
      });
      
      setEditingProduct({
        name: product.name,
        description: product.description,
        price: product.price,
        category: product.category,
        tags: product.tags.join(', '),
        previewImages: [],
        previewPreviews: [],
        existingPreviewImages: product.images?.map((url: string, index: number) => ({
          s3Key: undefined,
          url: url,
          caption: `Preview ${index + 1}`
        })) || [],
        deletedPreviewImages: [],
        downloadFile: null,
        downloadPreview: null
      });
      setEditDialogOpen(true);
    }
  };

  const handleEditSave = async () => {
    // #region agent log
    console.log('[DEBUG] handleEditSave entry:', { productId, hasProduct: !!product });
    fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:344',message:'handleEditSave entry',data:{productId,hasProduct:!!product},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A,B,C,D'})}).catch(()=>{});
    // #endregion
    if (!productId || !product) {
      console.log('[DEBUG] handleEditSave early return:', { productId, hasProduct: !!product });
      return;
    }

    // Debug: Log authentication status
    const authStatus = logAuthStatus('Edit Product Save');
    
    // Verify admin access and authentication before attempting save
    const token = localStorage.getItem('authToken');
    
    // #region agent log
    console.log('[DEBUG] Token check:', { hasToken: !!token, tokenLength: token?.length, userRole: user?.role });
    fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:352',message:'Token check',data:{hasToken:!!token,tokenLength:token?.length,userRole:user?.role},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    
    if (!token) {
      console.log('[DEBUG] No token found - returning early');
      console.warn('Edit Save: No token found');
      setSnackbar({ 
        open: true, 
        message: 'Your session has expired. Please log in again.', 
        severity: 'error' 
      });
      return;
    }

    // Check token validity
    const tokenValid = isTokenValid(token);
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:364',message:'Token validity check',data:{tokenValid},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion
    
    if (!tokenValid) {
      console.warn('Edit Save: Token is expired or invalid');
      localStorage.removeItem('authToken');
      setSnackbar({ 
        open: true, 
        message: 'Your session has expired. Please log out and log in again.', 
        severity: 'error' 
      });
      return;
    }

    // Check admin role from both user state and token
    const isAdminFromUser = user?.role === 'admin';
    const isAdminFromTokenCheck = isAdminFromToken(token);
    
    // #region agent log
    console.log('[DEBUG] Role check:', { isAdminFromUser, isAdminFromTokenCheck, userRole: user?.role, tokenRole: authStatus.role });
    fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:377',message:'Role check',data:{isAdminFromUser,isAdminFromTokenCheck,userRole:user?.role,tokenRole:authStatus.role},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
    // #endregion
    
    if (!isAdminFromUser && !isAdminFromTokenCheck) {
      console.log('[DEBUG] Not admin - returning early');
      console.warn('Edit Save: User does not have admin role', {
        userRole: user?.role,
        tokenRole: authStatus.role
      });
      setSnackbar({ 
        open: true, 
        message: 'You do not have permission to edit products. Admin access required.', 
        severity: 'error' 
      });
      return;
    }

    // Log successful pre-flight checks
    console.log('Edit Save: Pre-flight checks passed', {
      hasToken: true,
      tokenValid: true,
      isAdmin: true,
      productId
    });

    setSaving(true);
    try {
      // Check if we have files to upload OR deletions to process
      const hasNewFiles = (editingProduct.previewImages && editingProduct.previewImages.length > 0) || editingProduct.downloadFile;
      const hasDeletions = editingProduct.deletedPreviewImages && editingProduct.deletedPreviewImages.length > 0;
      
      console.log('[DEBUG] File/deletion check:', { hasNewFiles, hasDeletions, previewImagesCount: editingProduct.previewImages?.length || 0, deletedCount: editingProduct.deletedPreviewImages?.length || 0 });

      if (hasNewFiles || hasDeletions) {
        // Use FormData for file uploads and deletions
        const formData = new FormData();
        
        // Add preview images if any
        if (editingProduct.previewImages && editingProduct.previewImages.length > 0) {
          editingProduct.previewImages.forEach((file) => {
            formData.append('previewImages', file);
          });
        }
        
        // Add deleted preview images info
        if (editingProduct.deletedPreviewImages && editingProduct.deletedPreviewImages.length > 0) {
          formData.append('deletedPreviewImages', JSON.stringify(editingProduct.deletedPreviewImages));
        }
        
        // Add download file if provided
        if (editingProduct.downloadFile) {
          formData.append('downloadFile', editingProduct.downloadFile);
        }
        
        // Add product data
        formData.append('name', editingProduct.name || '');
        formData.append('description', editingProduct.description || '');
        formData.append('price', String(editingProduct.price || 0));
        formData.append('category', editingProduct.category || '');
        if (editingProduct.tags) {
          formData.append('tags', editingProduct.tags);
        }

        // #region agent log
        console.log('[DEBUG] Before FormData API call:', { productId, hasNewFiles, hasDeletions, formDataKeys: Array.from(formData.keys()) });
        fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:436',message:'Before API call',data:{productId,hasNewFiles,hasDeletions,formDataKeys:Array.from(formData.keys())},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
        // #endregion
        const response = await productService.updateProductWithFiles(productId, formData);
        // #region agent log
        console.log('[DEBUG] After FormData API call:', { success: response.success, message: response.message });
        fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:438',message:'After API call',data:{success:response.success,message:response.message},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
        // #endregion
        if (response.success) {
          setSnackbar({ open: true, message: response.message || 'Product updated successfully', severity: 'success' });
          setEditDialogOpen(false);
          // Cleanup object URLs
          editingProduct.previewPreviews?.forEach(url => URL.revokeObjectURL(url));
          if (editingProduct.downloadPreview) {
            URL.revokeObjectURL(editingProduct.downloadPreview);
          }
          // Refresh product data with cache-busting to ensure we get fresh data
          setTimeout(async () => {
            try {
              const freshResponse = await productService.getProduct(productId);
              if (freshResponse.success && freshResponse.data) {
                setProduct(freshResponse.data);
                console.log('[DEBUG] Product refreshed after FormData update:', freshResponse.data);
              }
            } catch (error) {
              console.error('Error refreshing product:', error);
              // Fallback to regular fetch
              await fetchProduct();
            }
          }, 1000);
        } else {
          throw new Error(response.message || 'Failed to update product');
        }
      } else {
        // Use regular JSON update for text-only changes
        const updateData: Partial<Product> = {
          name: editingProduct.name,
          description: editingProduct.description,
          price: editingProduct.price,
          category: editingProduct.category,
          tags: editingProduct.tags 
            ? editingProduct.tags.split(',').map((t: string) => t.trim()).filter((t: string) => t.length > 0)
            : undefined
        };

        // #region agent log
        console.log('[DEBUG] Before JSON API call:', { productId, updateDataKeys: Object.keys(updateData) });
        fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:464',message:'Before JSON API call',data:{productId,updateDataKeys:Object.keys(updateData)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
        // #endregion
        const response = await productService.updateProduct(productId, updateData);
        // #region agent log
        console.log('[DEBUG] After JSON API call:', { success: response.success, message: response.message });
        fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:466',message:'After JSON API call',data:{success:response.success,message:response.message},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
        // #endregion
        if (response.success) {
          setSnackbar({ open: true, message: response.message || 'Product updated successfully', severity: 'success' });
          setEditDialogOpen(false);
          // Refresh product data with cache-busting to ensure we get fresh data
          setTimeout(async () => {
            try {
              const freshResponse = await productService.getProduct(productId);
              if (freshResponse.success && freshResponse.data) {
                setProduct(freshResponse.data);
                console.log('[DEBUG] Product refreshed after JSON update:', freshResponse.data);
              }
            } catch (error) {
              console.error('Error refreshing product:', error);
              // Fallback to regular fetch
              await fetchProduct();
            }
          }, 1000);
        } else {
          throw new Error(response.message || 'Failed to update product');
        }
      }
    } catch (error: any) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:504',message:'Edit save error',data:{errorMessage:error.message,errorName:error.name,errorStack:error.stack?.substring(0,200)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
      // #endregion
      console.error('Error updating product:', error);
      
      // Handle specific error cases
      let errorMessage = 'Failed to update product. Please try again.';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      // Provide more specific error messages
      if (errorMessage.includes('Authentication required') || errorMessage.includes('401') || errorMessage.includes('token') || errorMessage.includes('session')) {
        errorMessage = 'Your session has expired. Please log out and log in again.';
      } else if (errorMessage.includes('403') || errorMessage.includes('Admin access required')) {
        errorMessage = 'You do not have permission to edit products. Admin access required.';
      } else if (errorMessage.includes('404') || errorMessage.includes('not found')) {
        errorMessage = 'Product not found. It may have been deleted.';
      }
      
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    const newPreviews = newFiles.map(file => URL.createObjectURL(file));

    setEditingProduct({
      ...editingProduct,
      previewImages: [...(editingProduct.previewImages || []), ...newFiles],
      previewPreviews: [...(editingProduct.previewPreviews || []), ...newPreviews]
    });
  };

  const removePreviewImage = (index: number) => {
    const newImages = [...(editingProduct.previewImages || [])];
    const newPreviews = [...(editingProduct.previewPreviews || [])];
    
    // Revoke object URL
    if (newPreviews[index]) {
      URL.revokeObjectURL(newPreviews[index]);
    }
    
    newImages.splice(index, 1);
    newPreviews.splice(index, 1);
    
    setEditingProduct({
      ...editingProduct,
      previewImages: newImages,
      previewPreviews: newPreviews
    });
  };

  const handleDownloadFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Revoke old preview if exists
    if (editingProduct.downloadPreview) {
      URL.revokeObjectURL(editingProduct.downloadPreview);
    }

    const preview = URL.createObjectURL(file);
    setEditingProduct({
      ...editingProduct,
      downloadFile: file,
      downloadPreview: preview
    });
  };

  const removeDownloadFile = () => {
    if (editingProduct.downloadPreview) {
      URL.revokeObjectURL(editingProduct.downloadPreview);
    }
    setEditingProduct({
      ...editingProduct,
      downloadFile: null,
      downloadPreview: null
    });
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:574',message:'handleDeleteConfirm entry',data:{productId,productIdFromProduct:product?.id,productIdFromUrl:productId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A,B,C,D'})}).catch(()=>{});
    // #endregion
    // Use productId from URL params as the source of truth (it's what the backend expects)
    // The product object might have id or _id, but the URL param is what we used to fetch it
    const idToDelete = productId || product?.id;
    
    if (!idToDelete) {
      console.error('Delete: No product ID available', { productId, productIdFromProduct: product?.id });
      setSnackbar({ open: true, message: 'Product ID is missing', severity: 'error' });
      return;
    }
    
    console.log('Delete: Using product ID:', { 
      idToDelete, 
      urlParam: productId, 
      productId: product?.id,
      productObject: product ? { id: product.id, name: product.name } : null
    });
    
    // Debug: Log authentication status
    const authStatus = logAuthStatus('Delete Product');
    
    // Verify admin access and authentication before attempting deletion
    const token = localStorage.getItem('authToken');
    
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:587',message:'Delete token check',data:{hasToken:!!token,tokenLength:token?.length,userRole:user?.role},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    
    if (!token) {
      console.warn('Delete: No token found');
      setSnackbar({ 
        open: true, 
        message: 'Your session has expired. Please log in again.', 
        severity: 'error' 
      });
      return;
    }

    // Check token validity
    const tokenValid = isTokenValid(token);
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:600',message:'Delete token validity',data:{tokenValid},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion
    
    if (!tokenValid) {
      console.warn('Delete: Token is expired or invalid');
      localStorage.removeItem('authToken');
      setSnackbar({ 
        open: true, 
        message: 'Your session has expired. Please log out and log in again.', 
        severity: 'error' 
      });
      return;
    }

    // Check admin role from both user state and token
    const isAdminFromUser = user?.role === 'admin';
    const isAdminFromTokenCheck = isAdminFromToken(token);
    
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:612',message:'Delete role check',data:{isAdminFromUser,isAdminFromTokenCheck,userRole:user?.role,tokenRole:authStatus.role},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
    // #endregion
    
    if (!isAdminFromUser && !isAdminFromTokenCheck) {
      console.warn('Delete: User does not have admin role', {
        userRole: user?.role,
        tokenRole: authStatus.role
      });
      setSnackbar({ 
        open: true, 
        message: 'You do not have permission to delete products. Admin access required.', 
        severity: 'error' 
      });
      return;
    }
    
    setDeleting(true);
    try {
      console.log('Attempting to delete product:', { 
        urlParam: productId, 
        productId: product?.id, 
        using: idToDelete,
        userRole: user?.role,
        tokenRole: authStatus.role,
        isAdmin: isAdminFromUser || isAdminFromTokenCheck
      });
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:639',message:'Before delete API call',data:{idToDelete},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      // #endregion
      console.log('Delete: Calling API with ID:', idToDelete);
      const response = await productService.deleteProduct(idToDelete);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:641',message:'After delete API call',data:{success:response?.success,message:response?.message},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
      // #endregion
      console.log('Delete response:', { 
        success: response?.success, 
        message: response?.message,
        fullResponse: response 
      });
      
      if (response && response.success) {
        console.log('Delete successful, navigating to home');
        setSnackbar({ open: true, message: response.message || 'Product deleted successfully', severity: 'success' });
        setDeleteDialogOpen(false);
        // Clear the product state immediately
        setProduct(null);
        // Navigate to home page after deletion
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        const errorMsg = response?.message || 'Failed to delete product';
        console.error('Delete failed:', { response, idToDelete });
        setSnackbar({ open: true, message: errorMsg, severity: 'error' });
        setDeleting(false);
      }
    } catch (error: any) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ProductListing.tsx:705',message:'Delete error',data:{errorMessage:error.message,errorName:error.name,errorStack:error.stack?.substring(0,200)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
      // #endregion
      console.error('Delete error:', error);
      // Handle ApiError or regular Error
      let errorMessage = 'Failed to delete product. Please try again.';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      // Provide more specific error messages
      if (errorMessage.includes('403') || errorMessage.includes('Admin access required')) {
        errorMessage = 'You do not have permission to delete products. Admin access required.';
      } else if (errorMessage.includes('401') || errorMessage.includes('token')) {
        errorMessage = 'Your session has expired. Please log in again.';
      } else if (errorMessage.includes('404') || errorMessage.includes('not found')) {
        errorMessage = 'Product not found. It may have already been deleted.';
      }
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading product...</Typography>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Product not found</Alert>
      </Container>
    );
  }

  // Create image array from product.images if available, otherwise use main image 3 times
  const images = product.images && product.images.length > 0 
    ? product.images 
    : [product.image, product.image, product.image];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Grid container spacing={4}>
        {/* Left Side - Image Gallery */}
        <Grid item xs={12} md={7}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Thumbnail Navigation */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {images.map((img, index) => (
                <Box
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  sx={{
                    width: 80,
                    height: 80,
                    border: selectedImageIndex === index ? '2px solid' : '1px solid',
                    borderColor: selectedImageIndex === index ? 'primary.main' : 'grey.300',
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.main' }
                  }}
                >
                  <img
                    src={img}
                    alt={`${product.name} ${index + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              ))}
            </Box>

            {/* Main Image */}
            <Box sx={{ flex: 1 }}>
              <Paper
                elevation={2}
                sx={{
                  width: '100%',
                  aspectRatio: '1',
                  overflow: 'hidden',
                  borderRadius: 2
                }}
              >
                <img
                  src={images[selectedImageIndex]}
                  alt={product.name}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Paper>
            </Box>
          </Box>
        </Grid>

        {/* Right Side - Product Info */}
        <Grid item xs={12} md={5}>
          <Box sx={{ position: 'sticky', top: 100 }}>
            {/* Favorite Button and Admin Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                {(() => {
                  const isAdmin = user?.role === 'admin';
                  // Debug logging for admin check
                  if (process.env.NODE_ENV === 'development') {
                    console.log('Admin button check:', { 
                      hasUser: !!user, 
                      userRole: user?.role, 
                      isAdmin,
                      userId: user?.id 
                    });
                  }
                  return isAdmin ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={handleEditClick}
                        sx={{ mr: 1 }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteClick}
                      >
                        Delete
                      </Button>
                    </Box>
                  ) : null;
                })()}
              </Box>
              <IconButton
                onClick={handleToggleFavorite}
                disabled={isLoadingFavorite}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                }}
              >
                {isFavorite ? (
                  <FavoriteIcon sx={{ color: '#e91e63' }} />
                ) : (
                  <FavoriteBorderIcon />
                )}
              </IconButton>
            </Box>

            {/* Price */}
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              ${product.price}
            </Typography>

            {/* Product Title */}
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {product.name}
            </Typography>

            {/* Seller & Rating */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Typography variant="body1" color="text.secondary">
                {product.author}
              </Typography>
              <Rating value={product.rating} precision={0.1} size="small" readOnly />
              <Typography variant="body2" color="text.secondary">
                ({product.rating})
              </Typography>
            </Box>

            {/* Add to Cart Button */}
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<CartIcon />}
              onClick={() => {
                // Check authentication before adding to cart
                if (!userService.isAuthenticated()) {
                  if (onRequireLogin) {
                    onRequireLogin();
                  } else {
                    alert('Please sign in to add items to your cart.');
                  }
                  return;
                }
                onAddToCart(product);
              }}
              sx={{
                py: 1.5,
                mb: 2,
                fontWeight: 600,
                fontSize: '1.1rem'
              }}
            >
              Add to cart
            </Button>

            {/* Item Details Accordion */}
            <Accordion defaultExpanded sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Item details
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <DesignIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={`Designed by ${product.author}`} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <BuildIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Supplies for making crafts" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CloudDownloadIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Digital download" />
                  </ListItem>
                </List>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Digital file type(s): {product.format}
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* Description */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Description
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {product.description}
              </Typography>
            </Box>

            {/* Tags */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {product.tags.map((tag, index) => (
                <Chip key={index} label={tag} size="small" />
              ))}
            </Box>

            {/* Category & Downloads */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip label={product.category} variant="outlined" />
              <Typography variant="body2" color="text.secondary">
                {product.downloads.toLocaleString()} downloads
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Reviews Section */}
      <Box sx={{ mt: 3, mb: 4 }}>
        <Grid container spacing={2}>
          {/* Left Column - Reviews */}
          <Grid item xs={12} md={8}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Reviews for this item ({reviewCount})
            </Typography>

            {/* Overall Rating and Detailed Ratings in same row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarIcon sx={{ fontSize: 40, color: '#FFC107' }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {averageRating.toFixed(1)}/5
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    item average
                  </Typography>
                </Box>
              </Box>
              
              {/* Detailed Ratings */}
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                icon={<StarIcon sx={{ color: '#FFC107' }} />}
                label={`${avgItemQuality > 0 ? avgItemQuality.toFixed(1) : averageRating.toFixed(1)} Item quality`}
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
              <Chip
                icon={<ShippingIcon sx={{ color: '#FFC107' }} />}
                label={`${avgShipping > 0 ? avgShipping.toFixed(1) : averageRating.toFixed(1)} Shipping`}
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
              <Chip
                icon={<SupportIcon sx={{ color: '#FFC107' }} />}
                label={`${avgCustomerService > 0 ? avgCustomerService.toFixed(1) : averageRating.toFixed(1)} Customer service`}
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
              <Chip
                icon={<CheckCircleIcon sx={{ color: '#4CAF50' }} />}
                label="100% Buyers recommend"
                variant="outlined"
                sx={{ fontWeight: 500, color: '#4CAF50' }}
              />
              </Box>
            </Box>

            {/* Buyer Highlights */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.75 }}>
                Buyer highlights, summarized by AI
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                {buyerHighlights.map((highlight, index) => (
                  <Chip
                    key={index}
                    label={highlight}
                    size="small"
                    sx={{ bgcolor: 'grey.100', fontWeight: 500 }}
                  />
                ))}
              </Box>

              {/* Filter Buttons */}
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                <Chip label="Ease of use (3)" variant="outlined" clickable />
                <Chip label="Appearance (1)" variant="outlined" clickable />
                <Chip label="Quality (1)" variant="outlined" clickable />
              </Box>
            </Box>

            {/* Individual Reviews */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {reviewsLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Loading reviews...
                  </Typography>
                </Box>
              ) : reviews.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No reviews yet. Be the first to review this product!
                  </Typography>
                </Paper>
              ) : (
                reviews.map((review) => (
                <Paper key={review.id} elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {review.reviewerName.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Rating value={review.rating} readOnly size="small" />
                        <Typography variant="body2" color="text.secondary">
                          {review.rating} This item
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {review.text}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {review.reviewerName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {review.date}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
                ))
              )}
            </Box>
          </Grid>

          {/* Right Column - Delivery & Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'sticky', top: 100 }}>
              {/* Delivery Section */}
              <Accordion defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Delivery
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                    Instant Download
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Your files will be available to download once payment is confirmed.{' '}
                    <Typography component="span" color="primary" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                      Here's how.
                    </Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Instant download items don't accept returns, exchanges or cancellations. Please contact the seller about any problems with your order.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              {/* Purchase Protection */}
              <Accordion defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Did you know?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircleIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      ArtFul Purchase Protection
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Shop confidently on ArtFul knowing if something goes wrong with an order, we've got your back for all eligible purchases —{' '}
                    <Typography component="span" color="primary" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                      see program terms
                    </Typography>
                  </Typography>
                </AccordionDetails>
              </Accordion>

              {/* Meet your sellers */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Meet your sellers
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {product.author.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {product.author}
                      </Typography>
                      <Rating value={product.rating} precision={0.1} size="small" readOnly />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Professional digital design creator specializing in high-quality stencils and SVG files.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              {/* Report Item Link */}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                <Typography component="span" color="primary" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                  Report this item to ArtFul.
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <Typography component="span" color="primary" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                  Learn more about this item
                </Typography>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Edit Product Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => {
          // Cleanup object URLs when closing
          editingProduct.previewPreviews?.forEach(url => URL.revokeObjectURL(url));
          if (editingProduct.downloadPreview) {
            URL.revokeObjectURL(editingProduct.downloadPreview);
          }
          setEditDialogOpen(false);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>Edit Product</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Product Name"
              fullWidth
              value={editingProduct.name || ''}
              onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
              required
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={4}
              value={editingProduct.description || ''}
              onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
              required
            />
            <TextField
              label="Price"
              type="number"
              fullWidth
              value={editingProduct.price || ''}
              onChange={(e) => setEditingProduct({ ...editingProduct, price: parseFloat(e.target.value) })}
              inputProps={{ min: 0, step: 0.01 }}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={editingProduct.category || ''}
                onChange={(e) => setEditingProduct({ ...editingProduct, category: e.target.value })}
                label="Category"
                required
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Tags (comma-separated)"
              fullWidth
              value={editingProduct.tags || ''}
              onChange={(e) => setEditingProduct({ ...editingProduct, tags: e.target.value })}
              placeholder="e.g., design, vector, modern"
            />

            {/* Existing Preview Images */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Existing Preview Images
              </Typography>
              {editingProduct.existingPreviewImages && editingProduct.existingPreviewImages.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                  {editingProduct.existingPreviewImages.map((preview, index) => {
                    const isDeleted = editingProduct.deletedPreviewImages?.includes(preview.s3Key || '');
                    return (
                      <Box 
                        key={preview.s3Key || `existing-${index}`} 
                        sx={{ 
                          position: 'relative', 
                          width: 100, 
                          height: 100,
                          opacity: isDeleted ? 0.5 : 1,
                          border: isDeleted ? '2px dashed' : '1px solid',
                          borderColor: isDeleted ? 'error.main' : 'grey.300',
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: isDeleted ? 'error.dark' : 'primary.main'
                          }
                        }}
                      >
                        <img
                          src={preview.url || preview.s3Key || ''}
                          alt={preview.caption || `Preview ${index + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            // Fallback if image fails to load
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23f5f5f5"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="Arial" font-size="12"%3EImage%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <IconButton
                          size="medium"
                          onClick={() => {
                            const deleted = editingProduct.deletedPreviewImages || [];
                            if (!isDeleted && preview.s3Key) {
                              setEditingProduct({
                                ...editingProduct,
                                deletedPreviewImages: [...deleted, preview.s3Key]
                              });
                            } else if (isDeleted && preview.s3Key) {
                              setEditingProduct({
                                ...editingProduct,
                                deletedPreviewImages: deleted.filter(key => key !== preview.s3Key)
                              });
                            }
                          }}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: isDeleted ? 'success.main' : 'error.main',
                            color: 'white',
                            width: 32,
                            height: 32,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            border: '2px solid white',
                            '&:hover': { 
                              bgcolor: isDeleted ? 'success.dark' : 'error.dark',
                              transform: 'scale(1.1)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                          title={isDeleted ? 'Restore image' : 'Delete image'}
                        >
                          {isDeleted ? <UndoIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                        {isDeleted && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              bgcolor: 'error.main',
                              color: 'white',
                              textAlign: 'center',
                              py: 0.5,
                              fontSize: '0.75rem'
                            }}
                          >
                            Will be deleted
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                  No existing preview images. Add new images below.
                </Typography>
              )}
            </Box>

            {/* Preview Images Upload */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Add New Preview Images (optional)
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="preview-images-upload"
                multiple
                type="file"
                onChange={handlePreviewImageChange}
              />
              <label htmlFor="preview-images-upload">
                <Button variant="outlined" component="span" size="small" sx={{ mb: 2 }}>
                  Add Preview Images
                </Button>
              </label>
              {editingProduct.previewPreviews && editingProduct.previewPreviews.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                  {editingProduct.previewPreviews.map((preview, index) => (
                    <Box key={index} sx={{ position: 'relative', width: 100, height: 100 }}>
                      <img
                        src={preview}
                        alt={`New Preview ${index + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removePreviewImage(index)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: 'error.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Download File Upload */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Download File (optional - leave empty to keep existing)
              </Typography>
              <input
                accept="*/*"
                style={{ display: 'none' }}
                id="download-file-upload"
                type="file"
                onChange={handleDownloadFileChange}
              />
              <label htmlFor="download-file-upload">
                <Button variant="outlined" component="span" size="small" sx={{ mb: 2 }}>
                  {editingProduct.downloadFile ? 'Change Download File' : 'Select Download File'}
                </Button>
              </label>
              {editingProduct.downloadFile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Typography variant="body2">{editingProduct.downloadFile.name}</Typography>
                  <IconButton size="small" onClick={removeDownloadFile} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              // Cleanup object URLs when canceling
              editingProduct.previewPreviews?.forEach(url => URL.revokeObjectURL(url));
              if (editingProduct.downloadPreview) {
                URL.revokeObjectURL(editingProduct.downloadPreview);
              }
              setEditDialogOpen(false);
            }} 
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleEditSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{product?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductListing;

